/**
 * User Preferences Service
 *
 * Stores user-specific preferences in Firebase Firestore.
 * Preferences are keyed by barber ID (from PIN login).
 *
 * Collection: userPreferences/{barberId}
 */

import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface UserPreferences {
  barberId: string;
  selectedBarbers?: string[]; // Calendar barber filter
  calendarView?: 'day' | 'week' | 'month';
  theme?: 'light' | 'dark' | 'system';
  updatedAt: string;
}

const COLLECTION = 'userPreferences';

class UserPreferencesService {
  // H12 FIX (Jose 2026-10-29 audit): value is `UserPreferences | null` so a barber with NO saved prefs
  // (the common first-login case) caches the null result. Previously null was returned WITHOUT caching,
  // so cache.has() stayed false and every access re-hit Firestore (repeat reads on every mount/switch).
  private cache: Map<string, UserPreferences | null> = new Map();

  /**
   * Get user preferences for a barber
   */
  async getPreferences(barberId: string): Promise<UserPreferences | null> {
    // Check cache first
    if (this.cache.has(barberId)) {
      return this.cache.get(barberId) || null;
    }

    try {
      const docRef = doc(db, COLLECTION, barberId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const prefs = docSnap.data() as UserPreferences;
        this.cache.set(barberId, prefs);
        return prefs;
      }
      this.cache.set(barberId, null); // H12: cache the "no prefs" result so we don't re-fetch every access
      return null;
    } catch (error) {
      console.error('[UserPreferences] Error getting preferences:', error);
      return null;
    }
  }

  /**
   * Save user preferences for a barber
   */
  async savePreferences(barberId: string, preferences: Partial<UserPreferences>): Promise<boolean> {
    try {
      const docRef = doc(db, COLLECTION, barberId);
      const updatedPrefs = {
        ...preferences,
        barberId,
        updatedAt: new Date().toISOString()
      };

      await setDoc(docRef, updatedPrefs, { merge: true });

      // Update cache
      const existing = this.cache.get(barberId) || { barberId, updatedAt: '' };
      this.cache.set(barberId, { ...existing, ...updatedPrefs });

      console.log('[UserPreferences] Saved preferences for barber:', barberId);
      return true;
    } catch (error) {
      console.error('[UserPreferences] Error saving preferences:', error);
      return false;
    }
  }

  /**
   * Get specific preference value
   */
  async getSelectedBarbers(barberId: string): Promise<string[]> {
    const prefs = await this.getPreferences(barberId);
    return prefs?.selectedBarbers || ['all'];
  }

  /**
   * Save selected barbers filter
   */
  async saveSelectedBarbers(barberId: string, selectedBarbers: string[]): Promise<boolean> {
    return this.savePreferences(barberId, { selectedBarbers });
  }

  /**
   * Get theme preference
   */
  async getTheme(barberId: string): Promise<'light' | 'dark' | 'system'> {
    const prefs = await this.getPreferences(barberId);
    return prefs?.theme || 'light';
  }

  /**
   * Save theme preference
   */
  async saveTheme(barberId: string, theme: 'light' | 'dark' | 'system'): Promise<boolean> {
    return this.savePreferences(barberId, { theme });
  }

  /**
   * Clear cache (useful when signing out)
   */
  clearCache(): void {
    this.cache.clear();
  }
}

export const userPreferencesService = new UserPreferencesService();
export default userPreferencesService;
